'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { IconPlus } from '@tabler/icons-react'
import { useSession } from 'next-auth/react'

import {
  AutoOpenModalAddDeliveryAddressManager,
  ModalAddDeliveryAddress,
  useCustomerAddresses
} from 'modules/customer'
import { FormMode } from 'shared/types/forms'
import Button from 'shared/ui/Button'
import { Listbox, ListboxItem } from 'shared/ui/Listbox'
import { noop } from 'shared/utils/common'
import { addTestId } from 'shared/utils/tests'

import useCartStore from '../../../store/cartStore'

import CartAddressOption from './CartAddressOption'

import styles from './CartAddDeliveryAddress.module.scss'

const CartAddDeliveryAddress = () => {
  const session = useSession()
  const sessionRef = useRef(session)
  const { deliveryAddresses: addresses } = useCustomerAddresses()
  const { cart, addAddressToCart } = useCartStore()

  sessionRef.current = session

  const [selectedAddressId, setSelectedAddressId] = useState<string | null>(
    null
  )
  const hasAddresses = useMemo(
    () => addresses && addresses.length > 0,
    [addresses]
  )

  useEffect(() => {
    if (!cart || !addresses || session.status !== 'authenticated') return

    const isValidAssignedDeliveryAddress =
      cart?.deliveryAddressId &&
      addresses.some(address => address.id === cart.deliveryAddressId)

    if (isValidAssignedDeliveryAddress) {
      setSelectedAddressId(cart?.deliveryAddressId)

      return
    }

    const defaultAddress = addresses?.find(a => a.isDefault) || addresses?.[0]

    if (defaultAddress) {
      addAddressToCart(defaultAddress.id, sessionRef.current)
        .then(() => {
          setSelectedAddressId(defaultAddress.id)
        })
        .catch(noop)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart?.deliveryAddressId, addresses, session.status])

  const handleAddAddressToCart = useCallback(
    (addressId: string) => {
      if (addressId === selectedAddressId) return

      let previousSelectedAddressId: string | null = null

      setSelectedAddressId(prev => {
        previousSelectedAddressId = prev

        return addressId
      })

      addAddressToCart(addressId, sessionRef.current).catch(() => {
        setSelectedAddressId(previousSelectedAddressId)
      })
    },
    [addAddressToCart, selectedAddressId]
  )

  return (
    <div className={styles.wrapper}>
      <h2 className={styles.title}>
        Adres dostawy{' '}
        <AutoOpenModalAddDeliveryAddressManager deliveryAddresses={addresses}>
          {({ isModalOpen, openModal, closeModal }) => (
            <ModalAddDeliveryAddress
              isOpen={isModalOpen}
              handleOpen={openModal}
              handleClose={closeModal}
              mode={FormMode.create}
              trigger={
                <Button
                  className={styles.addNewAddressButton}
                  fullWidth
                  size='medium'
                  variant='neutralText'
                  {...addTestId('add-delivery-address-button')}
                >
                  <IconPlus />
                  Dodaj nowy adres
                </Button>
              }
              isFirstAddressToAdd={!hasAddresses}
            />
          )}
        </AutoOpenModalAddDeliveryAddressManager>
      </h2>
      {hasAddresses ? (
        <Listbox
          id='delivery-addresses-list'
          selectedOptionId={selectedAddressId}
          onSelect={handleAddAddressToCart}
          {...addTestId('delivery-addresses-list')}
          ariaRequired={true}
        >
          {addresses?.map(el => (
            <ListboxItem key={el.id} optionId={el.id}>
              <CartAddressOption
                deliveryAddress={el}
                checked={selectedAddressId === el.id}
              />
            </ListboxItem>
          ))}
        </Listbox>
      ) : (
        <p className={styles.noItemsMessage}>
          Nie dodano jeszcze żadnego adresu.
        </p>
      )}
    </div>
  )
}

export default CartAddDeliveryAddress
